import React, { memo, useMemo } from 'react';
import { get as _get } from 'lodash';
import { Icon, MiniLoader } from '@pwc/pwc-deals-ui';
import { Constants, USER_ROLES } from '@pwc/pwc-deals-web-common';

import { useSelector } from 'react-redux';
import { getUserRole } from 'common/redux/security/loggedInUser/loggedInUserSlice';

// import TableauReport from 'features/analyses/TableauReport/TableauReport';
import { topicPanelType } from 'features/project/ViewProject/ProjectOverview/Topic/mapTopic';
import SyncedInfo from 'common/components/Autosync/SyncedInfo.tsx';
import PowerPivotLibraryItemCard from 'features/analyses/common/LibraryItemCard/PowerPivot/components/PowerPivotLibraryItemCard.tsx';
import UnableToLoadContent from '../../../../../analyses/common/UnableToLoadContent/UnableToLoadContent';
import PowerBiReport from '../../../../../analyses/PowerBiReport/PowerBiReport';
import {
  getIsPowerBiReport,
  getPowerBIPageNameFromTopic,
  getIsRTE,
} from '../../utils/helper';
import RichTextLibraryItem from '../../../../../../common/charts/types/ContentItem/RichTextLibraryItem';
import ExcelChartTopicPanel from './ExcelChartTopicPanel';
import './TopicPanel.scss';
import ExcelPPTLibraryItemCard from '../../../../../analyses/common/LibraryItemCard/ExcelPPTLibraryItemCard';
import {
  renderableContentTopic,
  renderableContentTypesFreeform,
} from '../../../../../../common/components/SectionArea/DraggableLibraryItemCard';
import TableauReport from '../../../../../analyses/TableauReport/TableauReport';

export const getClassName = (layoutType, panelNumber) => {
  let className = 'topic-panel';
  switch (layoutType) {
    case Constants.TOPIC_LAYOUT_TYPE.TWO_COLUMN.value: {
      if (panelNumber === 1) {
        className = `${className} topic-panel--equal-left-column`;
      } else {
        className = `${className} topic-panel--equal-right-column`;
      }
      break;
    }
    case Constants.TOPIC_LAYOUT_TYPE.THREE_COLUMN.value: {
      if (panelNumber === 1) {
        className = `${className} topic-panel--small-left-column`;
      } else if (panelNumber === 2) {
        className = `${className} topic-panel--middle-column`;
      } else {
        className = `${className} topic-panel--right-column`;
      }
      break;
    }
    case Constants.TOPIC_LAYOUT_TYPE.TWO_COLUMN_L_67_1A_R_33_1A.value: {
      if (panelNumber === 1) {
        className = `${className} topic-panel--big-left-column`;
      } else {
        className = `${className} topic-panel--right-column`;
      }
      break;
    }

    case Constants.TOPIC_LAYOUT_TYPE.TWO_COLUMN_L_50_2A_R_50_1A.value: {
      if (panelNumber === 1) {
        className = `${className} first-top-panel`;
      }
      if (panelNumber === 2) {
        className = `${className} right-big-top-panel`;
      }
      break;
    }

    case Constants.TOPIC_LAYOUT_TYPE.TWO_ROW_T_80_1A_B_20_1A.value: {
      if (panelNumber === 1) {
        className = `${className} topic-panel-stacked-large-top-small-bottom big-top-panel`;
      } else {
        className = `${className} topic-panel-stacked-large-top-small-bottom small-bottom-panel`;
      }
      break;
    }

    case Constants.TOPIC_LAYOUT_TYPE.TWO_ROW_T_60_1A_B_40_1A.value: {
      if (panelNumber === 1) {
        className = `${className} topic-panel--stacked-medium-top-row`;
      } else {
        className = `${className} topic-panel--stacked-medium-bottom-row`;
      }
      break;
    }

    case Constants.TOPIC_LAYOUT_TYPE.TWO_ROW_T_50_1A_B_50_2A.value: {
      if (panelNumber === 1) {
        className = `${className} topic-panel--full-column`;
      } else if (panelNumber === 2) {
        className = `${className} topic-panel--equal-left-column`;
      } else {
        className = `${className} topic-panel--equal-right-column`;
      }
      break;
    }
    default:
  }
  return className;
};

const DeletedContentWarning = () => (
  <div data-testid="deleted-content-warning">
    <h5>
      <Icon name="alert" className="fill icon-yellow" width="20" />
      &nbsp; Content Not Found
    </h5>
    <p>
      The content cannot be displayed as it has been deleted. Please update or
      delete this topic.
    </p>
  </div>
);

const RenderTextContent = memo(({ panel, hasContentError, projectId }) => {
  const isContainError = hasContentError || panel.error;

  if (panel.type === Constants.TOPIC_PANEL_TYPES.RICH_TEXT.value) {
    return isContainError ? (
      <UnableToLoadContent
        type={panel.statusType}
        placeId="content-error-topicPanel-text"
        projectId={projectId}
      />
    ) : (
      <RichTextLibraryItem
        item={{
          richTextContent: {
            richTextData: panel.sharedContent || panel.content,
          },
        }}
      />
    );
  }

  return null;
});

const RenderExcelAndPTT = memo(
  ({ topic, panel, projectId, excelPPTItem, hasContentError, isPwcUser }) => {
    const canRenderContentTopic = renderableContentTopic.includes(panel.type);
    const canRenderContentTypesFreeForm =
      renderableContentTypesFreeform.includes(panel.type);
    const isContainError = hasContentError || panel.error;
    const err = panel.error?.message || panel.error || {};

    if (canRenderContentTopic || canRenderContentTypesFreeForm)
      return isContainError ? (
        <UnableToLoadContent
          type={panel?.statusType || excelPPTItem?.type}
          data={{
            sharePointItemUrl: excelPPTItem?.sharePointItemUrl,
            worksheetName:
              excelPPTItem?.autoSync &&
              excelPPTItem?.autoSync.data.worksheetName,
            chartName:
              excelPPTItem?.autoSync && excelPPTItem?.autoSync.data.chartName,
            groupName:
              excelPPTItem?.autoSync && excelPPTItem?.autoSync.data.groupName,
            slideNumber: excelPPTItem?.slideNumber,
            name: excelPPTItem?.autoSync && excelPPTItem?.autoSync.data.name,
          }}
          errorCode={
            panel.errorCode ||
            err.errorCode ||
            panel.apiErrorCode ||
            err.apiErrorCode
          }
          apiErrorCode={err.apiErrorCode}
          placeId="content-error-topicPanel-excelAndPPT"
          projectId={projectId}
        />
      ) : (
        <>
          {canRenderContentTopic && (
            <div className="content-item-container-scroll">
              <ExcelChartTopicPanel
                topic={topic}
                panel={panel}
                projectId={projectId}
              />
            </div>
          )}
          {canRenderContentTypesFreeForm && (
            <ExcelPPTLibraryItemCard
              libraryItem={panel}
              projectId={projectId}
              setIsDragDisabled={() => {}}
            />
          )}
          {excelPPTItem && !canRenderContentTypesFreeForm && (
            <SyncedInfo
              projectId={projectId}
              excelPPTItem={excelPPTItem}
              isPwcUser={isPwcUser}
            />
          )}
        </>
      );

    return null;
  }
);

RenderExcelAndPTT.displayName = 'RenderExcelAndPTT';

const RenderDeletedContent = memo(({ panel, hypothesis }) => {
  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;
  const canRender =
    panel.type === Constants.TOPIC_PANEL_TYPES.KEY_FOCUS_AREA.value &&
    !hypothesis &&
    isPwcUser;

  if (canRender) return <DeletedContentWarning />;

  return null;
});

// READ ONLY mode of topics
const TopicPanel = memo(
  ({
    topic,
    topicLayout,
    panel,
    hypothesis,
    loading,
    projectId,
    excelPPTItem,
    hasContentError,
    dynamicExcelObject,
  }) => {
    const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;

    const className = useMemo(
      () => getClassName(topicLayout, panel.panelNumber),
      [topicLayout, panel]
    );
    const isPowerBiReport = useMemo(() => getIsPowerBiReport(panel), [panel]);
    const isRTE = useMemo(() => getIsRTE(panel), [panel]);

    const isLoading = useMemo(
      () => !hasContentError && (loading || !panel || !topic),
      [loading, panel, topic, hasContentError]
    );

    if (isLoading)
      return (
        <div data-testid="topic-panel" className={className}>
          <MiniLoader dataTestId="loading-spinner" />
        </div>
      );

    const predefinedPageName = getPowerBIPageNameFromTopic(topic, panel);

    const isTableauReport =
      (panel.type === topicPanelType.TableauReport ||
        panel.type === topicPanelType.TableauReportPage) &&
      panel.libraryItemId;

    return (
      <div data-testid="topic-panel" className={className}>
        {isRTE && (
          <RenderTextContent
            topic={topic}
            panel={panel}
            projectId={projectId}
            hasContentError={hasContentError}
          />
        )}
        {isTableauReport && (
          <TableauReport
            item={{
              ...panel,
              name:
                _get(panel, 'name', undefined) ||
                _get(panel, ['data', 'name'], undefined),
              tabName:
                _get(panel, 'tabName', undefined) ||
                _get(panel, ['data', 'tabName'], undefined),
            }}
            projectId={projectId}
            showLoadingMsg={false}
          />
        )}
        {isPowerBiReport ? (
          <PowerBiReport
            item={panel}
            projectId={projectId}
            predefinedPageName={predefinedPageName}
            hasContentError={hasContentError}
          />
        ) : (
          <RenderExcelAndPTT
            topic={topic}
            panel={panel}
            projectId={projectId}
            excelPPTItem={excelPPTItem}
            hasContentError={hasContentError}
            isPwcUser={isPwcUser}
          />
        )}
        {dynamicExcelObject ? (
          <PowerPivotLibraryItemCard
            libraryItem={{
              ...dynamicExcelObject,
              data: JSON.stringify({
                ...dynamicExcelObject.data,
              }),
              status: dynamicExcelObject.sharedStatus,
            }}
          />
        ) : null}
        <RenderDeletedContent panel={panel} hypothesis={hypothesis} />
      </div>
    );
  }
);

TopicPanel.displayName = 'TopicPanel';

export default TopicPanel;
